/* Version: 3.5.149 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { forwardRef } from 'react';
import { ButtonLeftOutlined, ButtonRightOutlined, ButtonLeftFilled, ButtonRightFilled, ExternalLink } from '@manulife/mux-cds-icons';
import { cva, cx } from 'class-variance-authority';
import PropTypes from 'prop-types';
import { SIDES } from '../constants.js';
import Progress from '../Progress/index.js';
import { safeRest, rem, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import deepMerge from '../../utils/deepMerge.js';
import { BUTTON_VARIANT } from './constants.js';
import { ButtonWrapper } from './styledComponents.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["ariaLabel", "children", "className", "customStyle", "disabled", "icon", "iconSide", "isExternal", "hoverIcon", "id", "onClick", "saving", "small", "variant"];
var buttonClasses = cva('mux-Button', {
  variants: {
    small: {
      false: null,
      true: 'mux-Button-small'
    },
    variant: _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, BUTTON_VARIANT.PRIMARY, 'mux-Button-primary'), BUTTON_VARIANT.SECONDARY, 'mux-Button-secondary'), BUTTON_VARIANT.TERTIARY, 'mux-Button-tertiary'), BUTTON_VARIANT.TERTIARY_BACK, 'mux-Button-tertiary'), BUTTON_VARIANT.TERTIARY_NOICON, 'mux-Button-tertiary'), BUTTON_VARIANT.SIGN_IN, 'mux-Button-sign-in')
  },
  defaultVariants: {
    small: false,
    variant: BUTTON_VARIANT.PRIMARY
  }
});
var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _theme$button;
  var ariaLabel = _ref.ariaLabel,
    children = _ref.children,
    className = _ref.className,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      buttonStyle: {}
    } : _ref$customStyle,
    disabled = _ref.disabled,
    icon = _ref.icon,
    _ref$iconSide = _ref.iconSide,
    iconSide = _ref$iconSide === undefined ? SIDES.LEFT : _ref$iconSide,
    isExternal = _ref.isExternal,
    _ref$hoverIcon = _ref.hoverIcon,
    hoverIcon = _ref$hoverIcon === undefined ? icon : _ref$hoverIcon,
    controlledId = _ref.id,
    _ref$onClick = _ref.onClick,
    onClick = _ref$onClick === undefined ? noop : _ref$onClick,
    saving = _ref.saving,
    _ref$small = _ref.small,
    small = _ref$small === undefined ? false : _ref$small,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? BUTTON_VARIANT.PRIMARY : _ref$variant,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('button-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    buttonStyle = _safeCustomStyle.buttonStyle;
  var theme = useTheme();
  var variantTheme = deepMerge(theme.button, (_theme$button = theme.button) === null || _theme$button === undefined || (_theme$button = _theme$button.variant) === null || _theme$button === undefined ? undefined : _theme$button[variant]);

  // build the Saving Spinner if required
  var progressProps = {
    size: small ? 16 : 20,
    magnitude: 7,
    isFloating: false,
    id: "".concat(id, "-spinner")
  };
  var Spinner = /*#__PURE__*/jsx("div", {
    className: "mux-Button-spinner",
    "data-testid": "".concat(id, "-saving-test"),
    children: /*#__PURE__*/jsx(Progress, _objectSpread({}, progressProps))
  });
  var isTertiary = variant === BUTTON_VARIANT.TERTIARY || variant === BUTTON_VARIANT.TERTIARY_BACK;
  var defaultIcon = variant === BUTTON_VARIANT.TERTIARY_BACK ? /*#__PURE__*/jsx(ButtonLeftOutlined, {}) : /*#__PURE__*/jsx(ButtonRightOutlined, {});
  var defaultHoverIcon = variant === BUTTON_VARIANT.TERTIARY_BACK ? /*#__PURE__*/jsx(ButtonLeftFilled, {}) : /*#__PURE__*/jsx(ButtonRightFilled, {});
  var Icons = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: "mux-Button-icon",
      "data-testid": "icon-wrapper",
      children: icon || defaultIcon
    }), /*#__PURE__*/jsx("div", {
      className: "mux-Button-hover-icon",
      "data-testid": "hover-icon-wrapper",
      children: hoverIcon || defaultHoverIcon
    })]
  });
  var ExternalIcon = /*#__PURE__*/jsx(ExternalLink, {
    fontSize: rem(18),
    "data-testid": "external-icon"
  });
  var RightSideIcon = (icon || hoverIcon || isTertiary) && iconSide === SIDES.RIGHT ? Icons : null;
  return /*#__PURE__*/jsxs(ButtonWrapper, _objectSpread(_objectSpread({
    "aria-label": ariaLabel,
    buttonStyle: buttonStyle,
    className: cx(buttonClasses({
      small: small,
      variant: variant
    }), className),
    "data-saving": saving,
    disabled: disabled,
    id: id,
    onClick: handle(onClick),
    ref: ref,
    theme: theme,
    variant: variant,
    variantTheme: variantTheme
  }, safeProps), {}, {
    children: [(icon || hoverIcon || isTertiary) && iconSide === SIDES.LEFT && Icons, children, saving && Spinner, !saving && isExternal ? ExternalIcon : RightSideIcon]
  }));
});
process.env.NODE_ENV !== "production" ? Button.propTypes = {
  /** Aria-label for button */
  ariaLabel: PropTypes.string,
  /** Content of the button */
  children: PropTypes.node.isRequired,
  /** Style overrides */
  customStyle: PropTypes.shape({
    buttonStyle: PropTypes.object
  }),
  /** Flag to disable button */
  disabled: PropTypes.bool,
  /**
   * Icon displayed on the button.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  icon: PropTypes.node,
  /**
   * Side where the icon should be placed
   */
  iconSide: PropTypes.oneOf([SIDES.LEFT, SIDES.RIGHT]),
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: PropTypes.string,
  /** External link indicator */
  isExternal: PropTypes.bool,
  /**
   * Icon displayed on the button when it is hovered or focused.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  hoverIcon: PropTypes.node,
  /** Click handler for button */
  onClick: PropTypes.func,
  /** Indicates button should show "saving" spinner */
  saving: PropTypes.bool,
  /** Specifies whether button should be small size (CDS/John Hancock) */
  small: PropTypes.bool,
  /** Type of button */
  variant: PropTypes.oneOf([BUTTON_VARIANT.PRIMARY, BUTTON_VARIANT.SECONDARY, BUTTON_VARIANT.TERTIARY, BUTTON_VARIANT.TERTIARY_BACK, BUTTON_VARIANT.TERTIARY_NOICON, BUTTON_VARIANT.SIGN_IN])
} : undefined;
process.env.NODE_ENV !== "production" ? Button.displayName = 'Button' : undefined;

export { Button as default };
